import * as Sentry from '@sentry/react';
import { SceneChat } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import { resolveOptimisticallyUpdatedSceneChats, updateActiveSceneChat } from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class SceneChatPollingStream extends PollingStream<SceneChat> {
	constructor(sceneChatId: string) {
		const url = VITE_FORGE_URL + `/poll/scene-chat/${sceneChatId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(updateActiveSceneChat(null));
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chats');
	}

	extractData(response: any): SceneChat[] {
		// Scene chat endpoint returns a single scene chat object or null
		if (response?.sceneChat === null || response?.sceneChat === undefined) {
			return [];
		}

		return [response.sceneChat];
	}

	onData(sceneChats: SceneChat[]) {
		// Resolve optimistic writes based on writeId from the database
		const sceneChat = sceneChats.length > 0 ? sceneChats[0] : null;
		if (!sceneChat) {
			store.dispatch(updateActiveSceneChat(null));
			return;
		}

		const writeId = sceneChat.writeId;
		if (writeId) {
			store.dispatch(resolveOptimisticallyUpdatedSceneChats([writeId]));
		}

		// The backend already returns SceneChat objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedSceneChat: SceneChat = {
			...sceneChat,
			createdAt: new Date(sceneChat.createdAt),
			updatedAt: new Date(sceneChat.updatedAt),
		};

		store.dispatch(updateActiveSceneChat(processedSceneChat));

		// Detect if optimistic updates have been applied
		// Get all write IDs that were resolved by the server
		const resolvedWriteIds = new Set(writeId ? [writeId] : []);

		// Check for any optimistic writes that weren't resolved
		const state = store.getState();
		const currentChatId = sceneChat.id;
		const unresolvedWrites = state.db.optimisticSceneChatWrites
			.filter((write) => write.value.id === currentChatId)
			.filter((write) => !resolvedWriteIds.has(write.id));

		if (unresolvedWrites.length > 0) {
			const now = Date.now();
			unresolvedWrites.forEach((write) => {
				if (write.value.createdAt instanceof Date) {
					const age = now - write.value.createdAt.getTime();
					if (age > 2500) {
						const message =
							`[PollingStream] Optimistic ${write.operation} for sceneChat ${write.value.id} ` +
							`not resolved after ${age}ms (writeId: ${write.id})`;
						console.warn(message);
						Sentry.captureMessage(message, 'warning');
					}
				}
			});
		}
	}
}
